<template>
  <base-keep-alive
    data-component="base-virtual-scroll-viewport"
    class="flex flex-col"
    :style="virtualViewportStyle"
  >
    <base-resize-observer
      v-if="isHeaderVisible && $slots['footer']"
      :key="HEADER_SLOT_KEY"
      @resize="$emit('header-slot-resize', $event)"
    >
      <div class="base-virtual-scroll-viewport-item">
        <slot name="header" />
      </div>
    </base-resize-observer>

    <base-virtual-scroll-viewport-item
      v-for="(item, vIndex) of visibleItems"
      :key="getItemId(item)"
      :item-id="getItemId(item)"
      :item-size="getItemSize(item)"
      :real-index="getRealIndex(vIndex)"
      :render-delay="renderDelay"
      @activation="onItemActivation(item)"
      @deactivation="onItemDeactivation(item)"
      @resize="onItemResize(item, $event)"
    >
      <template #default>
        <slot
          name="default"
          :index="getRealIndex(vIndex)"
          :data="item"
        />
      </template>
      <template #placeholder>
        <slot name="placeholder" />
      </template>
    </base-virtual-scroll-viewport-item>

    <base-resize-observer
      v-if="isFooterVisible && $slots['footer']"
      :key="FOOTER_SLOT_KEY"
      @resize="$emit('footer-slot-resize', $event)"
    >
      <div class="base-virtual-scroll-viewport-item">
        <slot name="footer" />
      </div>
    </base-resize-observer>

    <slot
      v-if="isLoading"
      name="loader"
    />
  </base-keep-alive>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue-demi';

import { BaseKeepAlive, BaseResizeObserver } from '../../decorators';
import {
  IResizeInfo,
  IVScrollItem,
} from '../../../types';
import { DEFAULT_RENDER_DELAY } from '../../../constants';
import BaseVirtualScrollViewportItem from './BaseVirtualScrollItemV2.vue';

/**
 * VScroll viewport renders only subset of items and adds padding for invisible items
 */
export default defineComponent({
  name: 'BaseVirtualScrollViewport',
  components: {
    BaseKeepAlive,
    BaseResizeObserver,
    BaseVirtualScrollViewportItem,
  },
  props: {
    isLoading: Boolean,
    isHeaderVisible: Boolean,
    isFooterVisible: Boolean,
    offsetTopPx: {
      type: Number,
      required: true,
    },
    offsetTopItems: {
      type: Number,
      required: true,
    },
    offsetBottomPx: {
      type: Number,
      required: true,
    },
    visibleItems: {
      type: Array as PropType<IVScrollItem[]>,
      required: true,
    },
    dataKey: {
      type: String,
      required: true,
    },
    getItemSize: {
      type: Function as PropType<(item: IVScrollItem) => number>,
      required: true,
    },
    renderDelay: {
      type: Number,
      default: DEFAULT_RENDER_DELAY,
    },
  },
  emits: ['item-resize', 'header-slot-resize', 'footer-slot-resize'],
  setup() {
    return {
      HEADER_SLOT_KEY: 'base-virtual-scroll-header',
      FOOTER_SLOT_KEY: 'base-virtual-scroll-footer',
    };
  },
  computed: {
    virtualViewportStyle(): Record<string, string> {
      return {
        paddingTop: `${this.offsetTopPx}px`,
        paddingBottom: `${this.offsetBottomPx}px`,
      };
    },
  },
  methods: {
    getItemId(item: IVScrollItem): string | number {
      return item[this.dataKey];
    },

    getRealIndex(index: number): number {
      return index + this.offsetTopItems;
    },

    onItemResize(item: IVScrollItem, resizeInfo: IResizeInfo): void {
      this.$emit('item-resize', { item, resizeInfo });
    },

    onItemActivation(item: IVScrollItem): void {
      this.$emit('item-activation', item);
    },

    onItemDeactivation(item: IVScrollItem): void {
      this.$emit('item-deactivation', item);
    },
  },
});
</script>

